/* ===========================================================
   files/local.js — files and folders from this computer

   File System Access API (Chrome, Edge, Opera): the person picks a
   file or folder, we get a handle to just that, and can read it and
   write straight back to it. Handles are remembered (IndexedDB) for a
   "recent" list; the browser asks again for permission when needed.
   Firefox and Safari don't have it: there we offer upload + download.
   =========================================================== */

import { newId } from '../../shared/protocol.js';

export const supported = () => typeof window !== 'undefined' && 'showOpenFilePicker' in window;

const CODE = {
  js: 'javascript', mjs: 'javascript', cjs: 'javascript', jsx: 'javascript', ts: 'typescript', tsx: 'typescript',
  py: 'python', sql: 'sql', html: 'html', htm: 'html', css: 'css', json: 'json', sh: 'bash', bat: 'bash', ps1: 'bash',
  java: 'java', c: 'c', h: 'c', cpp: 'cpp', cs: 'csharp', go: 'go', rs: 'rust', rb: 'ruby', php: 'php', yml: 'yaml', yaml: 'yaml', toml: 'toml', xml: 'xml',
};
export const extOf = name => (String(name).match(/\.([^.]+)$/)?.[1] || '').toLowerCase();
export function kindOf(name) {
  const e = extOf(name);
  if (e === 'md' || e === 'markdown') return 'md';
  if (e === 'txt' || e === 'text' || !e) return 'txt';
  if (CODE[e]) return 'code';
  return null;
}
export const langOf = name => CODE[extOf(name)] || '';
export const allowed = (name, dev) => { const k = kindOf(name); return k === 'md' || k === 'txt' || (dev && k === 'code'); };

export function pickerTypes(dev) {
  const types = [{ description: 'Documents', accept: { 'text/markdown': ['.md', '.markdown'], 'text/plain': ['.txt'] } }];
  if (dev) types.push({ description: 'Code', accept: { 'text/plain': Object.keys(CODE).map(e => `.${e}`) } });
  return types;
}

export async function ensurePermission(handle, ask = false) {
  const opts = { mode: 'readwrite' };
  if ((await handle.queryPermission?.(opts)) === 'granted') return true;
  if (!ask) return false;
  return (await handle.requestPermission?.(opts)) === 'granted';
}

export async function pickFile(dev) {
  const [handle] = await window.showOpenFilePicker({ types: pickerTypes(dev), excludeAcceptAllOption: false, multiple: false });
  return handle;
}

export async function pickFolder() {
  return window.showDirectoryPicker({ mode: 'readwrite' });
}

export async function listFolder(dir, dev) {
  const out = [];
  for await (const [name, h] of dir.entries()) {
    if (h.kind === 'file' && allowed(name, dev)) out.push({ name, handle: h });
    if (out.length > 500) break;
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

export const newKey = () => newId(12);

/* uploads (browsers without the API): kept in memory for this visit */
export const uploads = new Map();
